import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Card,
  DatePicker,
  Descriptions,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Tag,
  Typography,
  message,
} from 'antd';
import dayjs from 'dayjs';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import pmvService from '@/services/pmv.service';
import { useProjects } from '@/hooks/useProjects';
import {
  buildPmvProfileIdToLabelMap,
  buildProjectIdToLabelMap,
  resolvePmvProfileDisplayFromMap,
  resolveProjectDisplayFromMap,
} from '@/utils/pmvDisplay';

const { Text } = Typography;
const { TextArea } = Input;

const PmvAllocationWorkspacePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: projects = [] } = useProjects();
  const projectLabels = useMemo(() => buildProjectIdToLabelMap(projects), [projects]);
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferForm] = Form.useForm();

  const allocQ = useQuery({
    queryKey: ['pmv-allocation', id],
    queryFn: () => pmvService.getAllocation(id!),
    enabled: Boolean(id),
  });

  const profilesQ = useQuery({
    queryKey: ['pmv-profiles-labels'],
    queryFn: () => pmvService.listProfiles({ pageSize: 500 }),
  });

  const profileLabels = useMemo(
    () => buildPmvProfileIdToLabelMap(profilesQ.data?.data ?? []),
    [profilesQ.data?.data]
  );

  const alloc = allocQ.data;
  const dispatchId = (alloc as { dispatch_id?: string })?.dispatch_id;

  const dispatchMut = useMutation({
    mutationFn: () => pmvService.dispatchAllocation(id!),
    onSuccess: () => {
      message.success('Dispatch created');
      void qc.invalidateQueries({ queryKey: ['pmv-allocation', id] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  const gateOutMut = useMutation({
    mutationFn: () => pmvService.gateOutDispatch(dispatchId!),
    onSuccess: () => {
      message.success('Gate out recorded');
      void qc.invalidateQueries({ queryKey: ['pmv-allocation', id] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  const createLogMut = useMutation({
    mutationFn: () =>
      pmvService.createDailyLog({
        pmv_profile_id: alloc!.pmv_profile_id,
        allocation_id: id!,
        operating_date: new Date().toISOString().slice(0, 10),
        shift_code: 'DAY',
      }),
    onSuccess: (log) => {
      message.success('Daily log created');
      navigate(`/asset/pmv/daily-logs/${log.id}`);
    },
    onError: (e: Error) => message.error(e.message),
  });

  const logsQ = useQuery({
    queryKey: ['pmv-allocation-logs', id],
    queryFn: () => pmvService.listDailyLogs({ allocation_id: id, pageSize: 20 }),
    enabled: Boolean(id),
  });

  const releaseMut = useMutation({
    mutationFn: () => pmvService.releaseAllocation(id!, { release_reason: 'WORK_COMPLETED' }),
    onSuccess: () => {
      message.success('Released');
      void qc.invalidateQueries({ queryKey: ['pmv-allocation', id] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  const transferMut = useMutation({
    mutationFn: (body: {
      to_project_id: string;
      planned_start: string;
      planned_end: string;
      reason_text?: string | null;
    }) => pmvService.transferAllocation(id!, body),
    onSuccess: (res) => {
      message.success(`Transferred — new allocation ${res.new_allocation.allocation_number}`);
      setTransferOpen(false);
      transferForm.resetFields();
      navigate(`/asset/pmv/allocations/${res.new_allocation.id}`);
    },
    onError: (e: Error) => message.error(e.message),
  });

  const status = alloc?.allocation_status ?? '';
  const canDispatch = ['ALLOCATED', 'DISPATCH_READY'].includes(status);
  const canGateOut =
    dispatchId &&
    ['DISPATCH_READY'].includes((alloc as { dispatch_status?: string })?.dispatch_status ?? '');
  const canReceive =
    dispatchId &&
    ['DISPATCHED', 'IN_TRANSIT'].includes((alloc as { dispatch_status?: string })?.dispatch_status ?? '');
  const canRelease = ['ACTIVE', 'RECEIVED', 'DISPATCHED', 'ALLOCATED', 'DISPATCH_READY'].includes(status);
  const canTransfer = ['ACTIVE', 'RECEIVED'].includes(status);

  return (
    <WorkspaceLayout
      header={
        <EnterprisePageHeader
          documentLabel="PMV Allocation"
          documentNumber={alloc?.allocation_number ?? ''}
          statusTag={alloc ? <Tag>{alloc.allocation_status}</Tag> : undefined}
        />
      }
    >
      {alloc && (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Card size="small">
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Project">
                {resolveProjectDisplayFromMap(projectLabels, alloc.project_id)}
              </Descriptions.Item>
              <Descriptions.Item label="Period">
                {alloc.planned_start} → {alloc.planned_end}
              </Descriptions.Item>
              <Descriptions.Item label="PMV unit">
                <Button type="link" onClick={() => navigate(`/asset/pmv/${alloc.pmv_profile_id}`)}>
                  {resolvePmvProfileDisplayFromMap(profileLabels, alloc.pmv_profile_id)}
                </Button>
              </Descriptions.Item>
              {(alloc as { dispatch_status?: string }).dispatch_status && (
                <Descriptions.Item label="Dispatch">
                  {(alloc as { dispatch_status?: string }).dispatch_status}
                </Descriptions.Item>
              )}
            </Descriptions>
          </Card>
          <Space wrap>
            {canDispatch && (
              <Button type="primary" loading={dispatchMut.isPending} onClick={() => dispatchMut.mutate()}>
                Create dispatch
              </Button>
            )}
            {canGateOut && (
              <Button loading={gateOutMut.isPending} onClick={() => gateOutMut.mutate()}>
                Gate out
              </Button>
            )}
            {canReceive && (
              <Button onClick={() => navigate(`/asset/pmv/allocations/${id}/receive`)}>Site receipt</Button>
            )}
            {canTransfer && (
              <Button
                onClick={() => {
                  transferForm.setFieldsValue({
                    planned_start: dayjs(),
                    planned_end: dayjs().add(30, 'day'),
                  });
                  setTransferOpen(true);
                }}
              >
                Transfer to project
              </Button>
            )}
            {canRelease && status !== 'RELEASED' && (
              <Button danger loading={releaseMut.isPending} onClick={() => releaseMut.mutate()}>
                Release / Demobilize
              </Button>
            )}
            {['ACTIVE', 'RECEIVED'].includes(status) && (
              <Button type="primary" loading={createLogMut.isPending} onClick={() => createLogMut.mutate()}>
                Create daily log
              </Button>
            )}
            <Button onClick={() => navigate('/asset/pmv/requests')}>Requests</Button>
          </Space>
          {(logsQ.data?.data?.length ?? 0) > 0 && (
            <Card size="small" title="Daily logs">
              <Space direction="vertical">
                {logsQ.data!.data.map((l) => (
                  <Button key={l.id} type="link" onClick={() => navigate(`/asset/pmv/daily-logs/${l.id}`)}>
                    {l.log_number} — {l.operating_date} ({l.status})
                  </Button>
                ))}
              </Space>
            </Card>
          )}
          {status === 'RELEASED' && (
            <Text type="success">Allocation released — unit returned to pool per readiness.</Text>
          )}
        </Space>
      )}

      <Modal
        title="Transfer allocation to another project"
        open={transferOpen}
        onCancel={() => setTransferOpen(false)}
        destroyOnHidden
        footer={null}
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          Releases this allocation (actual end today) and creates a new allocation on the destination project so day
          history stays continuous for depreciation prorata.
        </Text>
        <Form
          form={transferForm}
          layout="vertical"
          onFinish={(v) => {
            void transferMut.mutate({
              to_project_id: v.to_project_id,
              planned_start: (v.planned_start as dayjs.Dayjs).format('YYYY-MM-DD'),
              planned_end: (v.planned_end as dayjs.Dayjs).format('YYYY-MM-DD'),
              reason_text: v.reason_text || null,
            });
          }}
        >
          <Form.Item name="to_project_id" label="Destination project" rules={[{ required: true }]}>
            <Select
              showSearch
              optionFilterProp="label"
              options={projects
                .filter((p) => p.id !== alloc?.project_id)
                .map((p) => ({
                  value: p.id,
                  label: resolveProjectDisplayFromMap(projectLabels, p.id),
                }))}
            />
          </Form.Item>
          <Form.Item name="planned_start" label="New planned start" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
          </Form.Item>
          <Form.Item name="planned_end" label="New planned end" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
          </Form.Item>
          <Form.Item name="reason_text" label="Reason">
            <TextArea rows={2} />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={transferMut.isPending} block>
            Confirm transfer
          </Button>
        </Form>
      </Modal>
    </WorkspaceLayout>
  );
};

export default PmvAllocationWorkspacePage;
